/**
 * journalCoverData — shared, null-safe data resolvers for the journal-
 * aesthetic cover layouts (boundary-plate / data-snapshot / masthead /
 * minimal / classic refinement).
 *
 * Every resolver reads EXISTING persisted report/theme/branding data — no
 * new fields, no migration. Data-Snapshot reads the TRACK-B snapshots
 * (`report.firmCapacityVerdict`, `report.pathwayLcoeAtGen`); legacy reports
 * that pre-date those fields fall back gracefully and NEVER produce NaN or
 * throw (per the cover pack's NO-DATA-LOSS rule).
 *
 * Pure + side-effect-free so the cover components stay presentational and
 * the resolvers are unit-testable in isolation.
 */
import type { SiteRagReport } from '../../types/siteRag';
import type { CoverBrandingModel } from '../../components/siterag/report/covers/types';
import { materializeSiteBoundaries, unionPolygonAreaAcres } from '../siteBoundaryGeometry';
import { getReportType } from '../../config/reportTypes';

/**
 * Acres recomputed LIVE from the report's boundary geometry — the SAME
 * source the in-plate area label draws from (HeroCoverBoundaryOverlay's
 * `polygonAreaSqMeters`). Returns `null` when the report carries no
 * drawable parcel polygon.
 *
 * Why this is geometry-first: the persisted `siteArea` / `totalArea`
 * scalars are documented as "computed from polygon" but can drift from
 * the live geometry (legacy reports stamped by an older area routine —
 * the ~10× cover discrepancy bug). When a parcel IS drawn, the byline /
 * Site-area chip MUST agree with the in-plate label, so we recompute
 * from the geometry rather than trust a possibly-stale stored scalar.
 */
export function geometryDerivedAcres(report: SiteRagReport): number | null {
  const parcels = materializeSiteBoundaries({
    siteBoundaries: report.siteBoundaries,
    siteBoundary: report.siteBoundary,
    siteArea: report.siteArea,
    sitePerimeter: report.sitePerimeter,
    siteCentroid: report.siteCentroid,
  });
  if (parcels.length === 0) return null;
  const acres = unionPolygonAreaAcres(parcels);
  return Number.isFinite(acres) && acres > 0 ? acres : null;
}

/**
 * P3 — THE ONE cover title resolver for all 7 cover variants.
 *
 * Single source of truth = `config/reportTypes.ts` labels (via getReportType().label).
 * Before P3, FOUR inline `switch (report.reportType)` blocks (Classic / Minimal /
 * DataDense / HeroImage) and this resolver each hardcoded the title independently —
 * which drifted: the journal path had NO `financial` case (fell to the BTM default),
 * returned 'Executive Summary Report' for the relabelled Go/No-Go Decision Guide,
 * and NONE of the seven handled `public-sentiment`. The inline strings also disagreed
 * with config ('Interconnection Feasibility Study' vs config's 'Connectivity
 * Feasibility Study'; 'Power Availability & BTM Analysis' vs 'Detailed Power
 * Availability Report'). Delegating to the config label fixes all of those at once
 * and keeps every cover in lock-step with future relabels.
 *
 * `custom-brief` keeps the user-named special case (customBriefName); `feasibility`
 * and any unmapped type fall to the registry label (feasibility → 'BTM Feasibility
 * Report'). Pure + null-safe — an unknown reportType falls through to feasibility's
 * label.
 */
export function resolveCoverTitle(report: SiteRagReport): string {
  if (report.reportType === 'custom-brief') {
    return report.customBriefName?.trim() || getReportType('custom-brief').label;
  }
  const type = report.reportType ?? 'feasibility';
  return getReportType(type).label;
}

/**
 * Title broken into display lines for the covers that lay the title out with
 * `<br/>` (ClassicCover, HeroImageCover). Splits the resolved title into short
 * balanced lines on word boundaries (target ≤ ~14 chars/line) so a long title
 * like 'Generation & Impact Report' wraps to a tidy stack rather than a
 * single overflowing line. The STRING source is still the one resolver above; only
 * the line presentation lives here. Deterministic (greedy word-pack, no layout
 * measurement) so the same title always wraps identically.
 */
export function resolveCoverTitleLines(report: SiteRagReport): string[] {
  const title = resolveCoverTitle(report);
  const words = title.split(/\s+/).filter(Boolean);
  if (words.length <= 1) return [title];
  const TARGET = 14;
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    if (current === '') {
      current = word;
    } else if ((current.length + 1 + word.length) <= TARGET) {
      current = `${current} ${word}`;
    } else {
      lines.push(current);
      current = word;
    }
  }
  if (current) lines.push(current);
  return lines;
}

/**
 * Short series/kicker label, e.g. "Datacrate · Screening Assessment".
 *
 * P3 — type-aware. Previously every cover read '{ORG} · BTM Feasibility'
 * regardless of report type, so a Financial / Public-Sentiment cover carried a
 * misleading "BTM Feasibility" series tag. Derive the series suffix from the
 * report type's registry shortLabel (e.g. 'Financial', 'Public Sentiment',
 * 'Connectivity') so the kicker matches the title. Falls back to the legacy
 * 'Screening Assessment' suffix when no report is supplied (back-compat with the
 * masthead's company-only call).
 */
export function resolveCoverKicker(companyName: string | undefined, report?: SiteRagReport): string {
  const brand = companyName?.trim() || 'Datacrate';
  if (!report) return `${brand} · Screening Assessment`;
  if (report.reportType === 'custom-brief') {
    const name = report.customBriefName?.trim();
    return `${brand} · ${name || getReportType('custom-brief').shortLabel}`;
  }
  const type = report.reportType ?? 'feasibility';
  return `${brand} · ${getReportType(type).shortLabel}`;
}

/**
 * The placeholder `resolveReportBranding` emits when no `SiteEntity` is resolved
 * (`reportBranding.ts` → `args.site?.name?.trim() || args.site?.id || 'Unnamed site'`).
 * It must NEVER win over a real `report.siteName` — see below.
 */
const UNNAMED_SITE_SENTINEL = 'Unnamed site';

/**
 * Resolve the site wordmark, preferring explicit branding then the report.
 *
 * N1.5 — the branding wordmark is `'Unnamed site'` whenever the cover renders
 * WITHOUT a resolved `SiteEntity` (the sample-PDF harness, archived reports, any
 * export-prop render with no store site). That sentinel is non-empty, so it
 * previously WON over `report.siteName`, surfacing "UNNAMED SITE" on the
 * EditorialMasthead cover even though the report carried a real site name.
 * Treat the sentinel as a fallthrough (the same guard `HeroImageCover` already
 * applies locally), so `report.siteName` is preferred when branding has nothing
 * real. Fixes every cover that calls this with `resolveReportBranding` output.
 */
export function resolveSiteWordmark(
  report: SiteRagReport,
  branding: CoverBrandingModel | undefined,
): string {
  const fromBranding = branding?.siteWordmark?.trim();
  const wordmark = (fromBranding && fromBranding !== UNNAMED_SITE_SENTINEL ? fromBranding : undefined)
    || report.siteName?.trim();

  if (wordmark) return wordmark;

  // NEVER print internal IDs (SED id "sed-...", registry ids) on a customer-facing cover
  const isCustomer = branding?.audienceMode === 'customer';
  const isInternalId = report.id && /^(sed|rep|reg)-/i.test(report.id);

  if (report.id && !(isCustomer && isInternalId)) {
    return report.id;
  }

  // FIX-B — last resort: NEVER ship the literal "Unnamed site" placeholder on
  // a real cover. When neither branding nor `report.siteName` nor a printable
  // `report.id` is available, fall back to the report/project title (the same
  // ONE resolver the cover title uses) so the wordmark reads as the document's
  // subject rather than a debug sentinel. (A thin sample/export fixture with no
  // site context is the common trigger; a genuine report always has a siteName.)
  return resolveCoverTitle(report);
}

/** Acres for the byline / Site-area chip. Geometry-FIRST: when the
 *  report carries a drawable parcel polygon we recompute the area live
 *  from that geometry so the byline agrees with the in-plate area label
 *  (fixes the ~10× cover discrepancy where a stale persisted scalar
 *  disagreed with the drawn parcel). Only when no geometry is present do
 *  we fall back to the persisted aggregate, then the deprecated scalar.
 *  `null` when none is a finite positive number. */
export function resolveSiteAcres(report: SiteRagReport): number | null {
  const fromGeometry = geometryDerivedAcres(report);
  if (fromGeometry != null) return fromGeometry;
  const agg = report.siteBoundaryAggregate?.totalArea;
  if (typeof agg === 'number' && Number.isFinite(agg) && agg > 0) return agg;
  const scalar = report.siteArea;
  if (typeof scalar === 'number' && Number.isFinite(scalar) && scalar > 0) return scalar;
  return null;
}

function formatAcres(acres: number): string {
  const rounded = Math.round(acres * 10) / 10;
  const text = Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toString();
  return `${text} acres`;
}

/** "Prepared for {customer}" — null when no customer company is named. */
export function resolvePreparedFor(report: SiteRagReport): string | null {
  const name = report.company?.name?.trim();
  if (!name) return null;
  return `Prepared for ${name}`;
}

/** Dotted byline fragments (already filtered of empties). Reused as the
 *  cover sub-rule / masthead affiliation / minimal byline. */
export function resolveBylineParts(
  report: SiteRagReport,
  branding: CoverBrandingModel | undefined,
): string[] {
  const parts: string[] = [];
  const addressRaw = report.address?.trim();
  // Strip a leading Plus-Code token ("9Q32+8G Moss Bluff, LA, USA" → "Moss Bluff,
  // LA, USA") so the cover byline reads a place name, not a geocode (golden-pass
  // 2026-06-27). Falls back to the raw address if stripping leaves it empty.
  const address = addressRaw
    ? addressRaw.replace(/^[23456789CFGHJMPQRVWX]{2,}\+[23456789CFGHJMPQRVWX]{2,}\s+/i, '').trim() || addressRaw
    : undefined;
  if (address) parts.push(address);
  const acres = resolveSiteAcres(report);
  if (acres != null) parts.push(formatAcres(acres));
  const prepared = resolvePreparedFor(report);
  if (prepared) parts.push(prepared);
  // `branding` reserved for future audience-aware byline tweaks; referenced
  // so callers can pass it uniformly.
  void branding;
  return parts;
}

/* ── Data-Snapshot — TRACK-B persisted snapshots (null-safe) ───────────── */

export interface DataSnapshotFigure {
  /** Stable id for keys + testids. */
  id: 'firm' | 'reserve' | 'lcoe' | 'cod';
  label: string;
  /** Headline value (never NaN — `null` collapses to an em-dash). */
  value: string | null;
  /** Small trailing unit, rendered de-emphasised. */
  unit?: string;
}

function roundMw(mw: number): string {
  return `${Math.round(mw)}`;
}

/** Worst-case firm verdict WORD from the persisted ELCC snapshot, mapped to
 *  the verdict vocabulary. `null` when no AND-combined phase was assessable
 *  (or the snapshot is absent on a legacy report). */
export function resolveFirmVerdictWord(report: SiteRagReport): string | null {
  const v = report.firmCapacityVerdict?.worstVerdict;
  switch (v) {
    case 'feasible':   return 'Feasible';
    case 'marginal':   return 'Conditional';
    case 'infeasible': return 'Infeasible';
    default:           return null;
  }
}

/** Lowest pathway LCOE (nominal USD/MWh) from the persisted snapshot.
 *  `null` when the array is absent/empty or carries no finite positive
 *  value — never NaN. */
export function resolvePathwayLcoeUsd(report: SiteRagReport): number | null {
  const rows = report.pathwayLcoeAtGen;
  if (!Array.isArray(rows) || rows.length === 0) return null;
  let best: number | null = null;
  for (const row of rows) {
    const v = row?.lcoeNominalPerMwhUsd;
    if (typeof v === 'number' && Number.isFinite(v) && v > 0) {
      best = best == null ? v : Math.min(best, v);
    }
  }
  return best;
}

/**
 * The four Data-Snapshot figures — firm deliverable, required firm capacity, LCOE at
 * gen, ultimate COD. Each is independently null-safe; a legacy report with
 * no TRACK-B snapshot yields four em-dash figures rather than throwing.
 */
export function resolveDataSnapshotFigures(report: SiteRagReport): DataSnapshotFigure[] {
  const fcv = report.firmCapacityVerdict;

  const firmMw =
    typeof fcv?.firmDeliverableMW === 'number' && Number.isFinite(fcv.firmDeliverableMW) && fcv.firmDeliverableMW > 0
      ? fcv.firmDeliverableMW
      : null;

  // Required firm capacity is a LOAD-derived gate, never available supply
  // grossed up by reserve. Read the matched frozen assessment (which also
  // honors a stronger declared firm target). Legacy snapshots omit the load
  // and cannot support this figure; do not guess at their percentage units.
  const comparison = fcv?.phaseComparison;
  const hasMatchedRequirement = comparison?.source === 'phase-feasibility-v1'
    && typeof comparison.phaseId === 'string' && comparison.phaseId.trim().length > 0
    && typeof comparison.phaseLabel === 'string' && comparison.phaseLabel === fcv?.worstPhaseLabel
    && ['feasible', 'marginal', 'infeasible'].includes(comparison.verdict)
    && comparison.verdict === fcv?.worstVerdict
    && typeof comparison.firmDeliverableMW === 'number'
    && Number.isFinite(comparison.firmDeliverableMW) && comparison.firmDeliverableMW >= 0
    && comparison.firmDeliverableMW === fcv?.firmDeliverableMW
    && comparison.reservePct === fcv?.reservePct
    && ['declared-exact', 'bucket-sizing-basis', 'site-fallback'].includes(comparison.loadBasis)
    && Number.isFinite(comparison.requiredMW) && comparison.requiredMW > 0
    && Number.isFinite(comparison.reservePct) && comparison.reservePct >= 0
    && Number.isFinite(comparison.requiredFirmMW) && comparison.requiredFirmMW > 0
    && comparison.requiredFirmMW + 1e-6 >= comparison.requiredMW * (1 + comparison.reservePct / 100);
  const reserveMw = hasMatchedRequirement ? comparison!.requiredFirmMW : null;
  const requirementLabel = hasMatchedRequirement && comparison?.loadBasis === 'bucket-sizing-basis'
    ? 'Required firm capacity (sizing basis)'
    : hasMatchedRequirement && comparison?.loadBasis === 'site-fallback'
      ? 'Required firm capacity (site fallback)'
      : 'Required firm capacity';
  const capacity = report.generationImpactProjection?.capacityAndResilience;
  const gridSupply = capacity?.capacityRole === 'grid-supply';
  const headlineMw = gridSupply
    ? (Number.isFinite(capacity.contingencyNetMW) && capacity.contingencyNetMW >= 0 ? capacity.contingencyNetMW : null)
    : firmMw;
  const headlineReserveMw = gridSupply
    ? (Number.isFinite(capacity.reserveRequiredMW) && capacity.reserveRequiredMW > 0 ? capacity.reserveRequiredMW : null)
    : reserveMw;

  const lcoeUsd = resolvePathwayLcoeUsd(report);

  const cod = resolveUltimateCod(report);

  return [
    {
      id: 'firm',
      label: gridSupply ? `Modeled ${capacity.criterion} grid supply` : 'Firm deliverable',
      value: headlineMw != null ? roundMw(headlineMw) : null,
      unit: headlineMw != null ? 'MW' : undefined,
    },
    {
      id: 'reserve',
      label: gridSupply ? 'Reserve need' : requirementLabel,
      value: headlineReserveMw != null ? roundMw(headlineReserveMw) : null,
      unit: headlineReserveMw != null ? 'MW' : undefined,
    },
    {
      id: 'lcoe',
      label: 'LCOE at gen',
      value: lcoeUsd != null ? `$${Math.round(lcoeUsd)}` : null,
      unit: lcoeUsd != null ? '/MWh' : undefined,
    },
    {
      id: 'cod',
      label: 'Ultimate COD',
      value: cod,
    },
  ];
}

/** Ultimate commercial-operation date — the latest phase COD year from the
 *  intent-faithful development pathway. `null` when no phase carries a
 *  4-digit year. */
export function resolveUltimateCod(report: SiteRagReport): string | null {
  const phases = report.developmentPathway;
  if (!Array.isArray(phases) || phases.length === 0) return null;
  let latest: number | null = null;
  for (const phase of phases) {
    const match = phase?.cod?.match(/\b(20\d{2})\b/);
    if (match) {
      const year = Number(match[1]);
      if (Number.isFinite(year)) latest = latest == null ? year : Math.max(latest, year);
    }
  }
  return latest != null ? String(latest) : null;
}

/** Locked fixed-page cover standfirst limit. The complete executive narrative
 * belongs to the canonical body; a cover may carry only this bounded lede. */
export const COVER_STANDFIRST_MAX_CHARS = 420;

export function truncateCoverStandfirst(
  raw: string | null | undefined,
  maxChars = COVER_STANDFIRST_MAX_CHARS,
): string | null {
  const oneLine = raw?.replace(/\s+/g, ' ').trim() ?? '';
  if (!oneLine) return null;
  if (oneLine.length <= maxChars) return oneLine;
  const cut = oneLine.slice(0, maxChars);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > 0 ? cut.slice(0, lastSpace) : cut).trim()}…`;
}

/** Bounded cover lede. It never replaces or suppresses the body Executive
 * Summary, even when the source summary happens to fit in full. */
export function resolveAbstractExcerpt(
  report: SiteRagReport,
  maxChars = COVER_STANDFIRST_MAX_CHARS,
): string | null {
  const raw =
    report.executiveSummary?.trim()
    || report.summary?.trim()
    || report.executive?.trim()
    || report.conclusion?.recommendation?.trim()
    || '';
  return truncateCoverStandfirst(raw, maxChars);
}

/** Cover abstract — the report's executive-summary-equivalent rendered IN
 *  FULL (no char-cap/truncation). When the cover carries the abstract it
 *  OWNS the exec summary and the standalone body section is suppressed, so
 *  the abstract must NOT be clamped. Whitespace is normalised to a single
 *  flowing paragraph but the content is never shortened. `null` when the
 *  report has no summary. */
export function resolveAbstractFull(report: SiteRagReport): string | null {
  const raw =
    report.executiveSummary?.trim()
    || report.summary?.trim()
    || report.executive?.trim()
    || report.conclusion?.recommendation?.trim()
    || '';
  if (!raw) return null;
  // Public-sentiment narratives contain saved Markdown chapters. Flattening
  // them turns the entire executive into a single heading before rendering.
  return report.reportType === 'public-sentiment' ? raw : raw.replace(/\s+/g, ' ').trim();
}

/** Index Terms for the masthead — report-type-derived keyword set. */
export function resolveIndexTerms(report: SiteRagReport): string[] {
  switch (report.reportType) {
    case 'power-availability':
      return ['behind-the-meter', 'ELCC firm capacity', 'interconnection queue', 'levelised cost'];
    case 'environmental':
      return ['NEPA screening', 'wetlands', 'air quality', 'critical habitat'];
    case 'water-resource':
      return ['water stress', 'cooling demand', 'consumptive use', 'watershed'];
    case 'test-fit':
      return ['site layout', 'campus footprint', 'phasing', 'developable area'];
    case 'interconnection':
      return ['interconnection queue', 'point of interconnection', 'firm capacity', 'transmission'];
    case 'supply-chain-risk':
      return ['supply chain', 'lead time', 'procurement risk', 'equipment'];
    default:
      return ['behind-the-meter', 'firm capacity', 'levelised cost', 'datacenter siting'];
  }
}
